import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { catsApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';

const EditCatPage = () => {
  const { catId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Form state
  const [name, setName] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [photoUploadStatus, setPhotoUploadStatus] = useState('');
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Fetch cat details to pre-fill form
  useEffect(() => {
    const fetchCat = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        
        const catData = await catsApi.getCatById(catId);
        
        // Set form data from cat data
        setName(catData.name || '');
        setGender(catData.gender || '');
        setAge(catData.age?.toString() || '');
        setDescription(catData.description || '');
        
        // Process existing photos
        let existingPhotoUrls = [];
        if (catData.catPhotos && catData.catPhotos.length > 0) {
          // Extract paths from the catPhotos objects
          existingPhotoUrls = catData.catPhotos.map(photo => photo.path);
        } else if (catData.catPhotoPaths && catData.catPhotoPaths.length > 0) {
          existingPhotoUrls = catData.catPhotoPaths;
        } else if (catData.mainPhoto) {
          existingPhotoUrls = [catData.mainPhoto];
        }
        
        setExistingPhotos(existingPhotoUrls);
        
      } catch (error) {
        console.error('Error fetching cat details:', error);
        setError(handleApiError(error));
      } finally {
        setInitialLoading(false);
      }
    };
    
    if (catId) {
      fetchCat();
    }
  }, [catId]);
  
  // Handle photo selection
  const handlePhotoChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Validate file size and type
    const validFiles = [];
    const invalidFiles = [];
    
    selectedFiles.forEach(file => {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        invalidFiles.push(`${file.name} (file too large, max 5MB)`);
        return;
      }
      
      // Check file type
      if (!file.type.match('image.*')) {
        invalidFiles.push(`${file.name} (not an image file)`);
        return;
      }
      
      validFiles.push(file);
    });
    
    // Check total number of photos
    if (photos.length + validFiles.length > 5) {
      alert('You can upload a maximum of 5 photos per cat.');
      return;
    }
    
    if (invalidFiles.length > 0) {
      alert(`Some files couldn't be added:\n${invalidFiles.join('\n')}`);
    }
    
    if (validFiles.length > 0) {
      setPhotos([...photos, ...validFiles]);
      
      // Create preview URLs
      const newPreviewImages = validFiles.map(file => URL.createObjectURL(file));
      setPreviewImages([...previewImages, ...newPreviewImages]);
      
      setPhotoUploadStatus(`${validFiles.length} photo${validFiles.length > 1 ? 's' : ''} added successfully.`);
      
      // Clear upload status after 3 seconds
      setTimeout(() => {
        setPhotoUploadStatus('');
      }, 3000);
    }
  };
  
  // Remove a photo from new uploads
  const removePhoto = (index) => {
    const newPhotos = [...photos];
    const newPreviewImages = [...previewImages];
    
    // Revoke the URL to prevent memory leaks
    URL.revokeObjectURL(newPreviewImages[index]);
    
    newPhotos.splice(index, 1);
    newPreviewImages.splice(index, 1);
    
    setPhotos(newPhotos);
    setPreviewImages(newPreviewImages);
  };
  
  // Remove an existing photo
  const removeExistingPhoto = (index) => {
    // Add confirmation
    if (!window.confirm('Are you sure you want to remove this photo?')) {
      return;
    }
    
    const newExistingPhotos = [...existingPhotos];
    newExistingPhotos.splice(index, 1);
    setExistingPhotos(newExistingPhotos);
    
    if (currentPhotoIndex >= newExistingPhotos.length) {
      setCurrentPhotoIndex(Math.max(0, newExistingPhotos.length - 1));
    }
  };
  
  // Navigate to previous photo
  const prevPhoto = () => {
    if (existingPhotos.length <= 1) return;
    setCurrentPhotoIndex((prevIndex) => 
      (prevIndex - 1 + existingPhotos.length) % existingPhotos.length
    );
  };
  
  // Navigate to next photo
  const nextPhoto = () => {
    if (existingPhotos.length <= 1) return;
    setCurrentPhotoIndex((prevIndex) => 
      (prevIndex + 1) % existingPhotos.length
    );
  };
  
  // Handle age input (ensure it's a positive integer)
  const handleAgeChange = (e) => {
    const value = e.target.value;
    // Allow empty string or positive integers only
    if (value === '' || /^[0-9]+$/.test(value)) {
      setAge(value);
    }
  };
  
  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handlePhotoChange({ target: { files: e.dataTransfer.files } });
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!name || !gender) {
      setError('Name and gender are required fields');
      return;
    }
    
    // Validate age if provided (should be a positive integer)
    if (age && (isNaN(parseInt(age)) || parseInt(age) < 0)) {
      setError('Age must be a positive number of months');
      return;
    }
    
    // Check if at least one photo will remain (either existing or new)
    if (existingPhotos.length === 0 && photos.length === 0) {
      setError('Please provide at least one photo');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', name);
      formData.append('gender', gender);
      if (age) formData.append('age', parseInt(age)); // Convert to integer
      if (description) formData.append('description', description);
      
      // Append all new photos to the formData
      photos.forEach((photo) => {
        formData.append('files', photo);
      });
      
      // Append existing photo paths that should be kept
      existingPhotos.forEach((photoPath) => {
        formData.append('existingPhotoPaths', photoPath);
      });
      
      // Update cat via API
      await catsApi.updateCat(catId, formData);
      
      // Redirect to cat details page with success message
      navigate(`/feeder/cat/${catId}`, { 
        state: { success: true, message: `${name} has been updated successfully!` } 
      });
    } catch (error) {
      console.error('Error updating cat:', error);
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="edit-cat-page">
      <div className="page-header">
        <h1 className="page-title">Edit Cat</h1>
        <p className="page-description">
          Update your cat's information
        </p>
      </div>
      
      {/* Loading state */}
      {initialLoading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button className="close-button" onClick={() => setError(null)}>×</button>
        </div>
      )}
      
      {!initialLoading && (
        <div className="card">
          <form onSubmit={handleSubmit} className="edit-cat-form">
            <div className="form-group">
              <label htmlFor="name">Cat Name *</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter cat name"
                required
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="gender">Gender *</label>
              <select
                id="gender"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                required
                className="form-control"
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="age">Age (in months)</label>
              <input
                type="number"
                id="age"
                value={age}
                onChange={handleAgeChange}
                placeholder="Enter age in months"
                min="0"
                className="form-control"
              />
              <small className="helper-text">Please enter the cat's age in months (e.g., 18 for a 1.5-year-old cat)</small>
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the cat (appearance, behavior, location, etc.)"
                className="form-control"
                rows="4"
              ></textarea>
            </div>
            
            {/* Current Photos Section */}
            {existingPhotos.length > 0 && (
              <div className="form-group">
                <label>Current Photos</label>
                <div className="existing-photos-container">
                  <div className="main-photo">
                    <img 
                      src={existingPhotos[currentPhotoIndex]}
                      alt={`${name} - current photo ${currentPhotoIndex + 1}`}
                      onError={(e) => { e.target.src = '/cat-placeholder.png'; }}
                    />
                    
                    {existingPhotos.length > 1 && (
                      <>
                        <button 
                          type="button"
                          className="photo-nav-button prev-button" 
                          onClick={prevPhoto}
                          aria-label="Previous photo"
                        >
                          ‹
                        </button>
                        <button 
                          type="button"
                          className="photo-nav-button next-button" 
                          onClick={nextPhoto}
                          aria-label="Next photo"
                        >
                          ›
                        </button>
                      </>
                    )}
                    
                    <button 
                      type="button"
                      className="remove-photo-btn remove-existing-photo-btn"
                      onClick={() => removeExistingPhoto(currentPhotoIndex)}
                      disabled={loading || existingPhotos.length + photos.length <= 1}
                      title={existingPhotos.length + photos.length <= 1 ? "At least one photo is required" : "Remove this photo"}
                    >
                      ✕
                    </button>
                  </div>
                  
                  <p className="photo-help-text">
                    {existingPhotos.length + photos.length <= 1 
                      ? "At least one photo is required. Add a new photo before removing this one." 
                      : "Click the × to remove a photo"}
                  </p>
                  
                  {existingPhotos.length > 1 && (
                    <div className="photo-thumbnails">
                      {existingPhotos.map((photo, index) => (
                        <div 
                          key={index}
                          className={`photo-thumbnail ${index === currentPhotoIndex ? 'active' : ''}`}
                          onClick={() => setCurrentPhotoIndex(index)}
                        >
                          <img 
                            src={photo} 
                            alt={`${name} - thumbnail ${index + 1}`}
                            onError={(e) => { e.target.src = '/cat-placeholder.png'; }}
                          />
                          <button 
                            type="button"
                            className="thumbnail-remove-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeExistingPhoto(index);
                            }}
                            disabled={loading || existingPhotos.length + photos.length <= 1}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* New Photos Section */}
            <div className="form-group">
              <label>{existingPhotos.length > 0 ? 'Add New Photos' : 'Photos *'}</label>
              <div 
                className="photo-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoChange}
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                />
                
                {photoUploadStatus && (
                  <div className="upload-status">
                    {photoUploadStatus}
                  </div>
                )}
                
                <div className={`photo-grid ${photos.length === 0 ? 'empty' : ''}`}>
                  {/* Drag & drop area when no photos */}
                  {photos.length === 0 && (
                    <div 
                      className="drag-drop-area"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <div className="upload-icon">📷</div>
                      <p>Drag & drop photos here or click to browse</p>
                      <p className="small-text">Max 5 photos, 5MB each</p>
                    </div>
                  )}
                  
                  {/* Preview images */}
                  {previewImages.map((previewUrl, index) => (
                    <div key={index} className="photo-preview">
                      <img src={previewUrl} alt={`Preview ${index + 1}`} />
                      <button 
                        type="button" 
                        className="remove-photo-btn"
                        onClick={() => removePhoto(index)}
                        disabled={loading}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  
                  {/* Add photo button */}
                  {photos.length < 5 && photos.length > 0 && (
                    <div 
                      className="add-photo-btn"
                      onClick={() => fileInputRef.current.click()}
                    >
                      <div className="add-icon">+</div>
                      <span>Add Photo</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="form-buttons">
              <button
                type="button"
                onClick={() => navigate(`/feeder/cat/${catId}`)}
                className="button secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="button"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EditCatPage; 